import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import type {
  WhatsAppCapabilities,
  WhatsAppOverview,
  WhatsAppRecentActivityItem,
  WhatsAppTemplate,
} from '../models/whatsapp-settings.models';

type JsonRecord = Record<string, unknown>;

@Injectable({ providedIn: 'root' })
export class WhatsAppSettingsApiService {
  private readonly http = inject(HttpClient);

  async getOverview(): Promise<WhatsAppOverview> {
    const response = await firstValueFrom(this.http.get<unknown>('/api/whatsapp/overview'));
    return this.mapOverview(response);
  }

  async getTemplates(): Promise<WhatsAppTemplate[]> {
    const response = await firstValueFrom(this.http.get<unknown>('/api/whatsapp/templates'));
    return this.asArray(this.unwrapItems(response))
      .map((entry) => this.mapTemplate(entry))
      .filter((item) => item.name.length > 0);
  }

  async getCapabilities(): Promise<WhatsAppCapabilities> {
    const response = await firstValueFrom(this.http.get<unknown>('/api/whatsapp/capabilities'));
    return this.mapCapabilities(response);
  }

  async getRecentActivity(): Promise<WhatsAppRecentActivityItem[]> {
    const response = await firstValueFrom(this.http.get<unknown>('/api/whatsapp/recent-activity'));
    return this.asArray(this.unwrapItems(response))
      .map((entry) => this.mapRecentActivity(entry))
      .filter((item) => !!item.occurredAt);
  }

  private mapOverview(raw: unknown): WhatsAppOverview {
    const source = this.asRecord(raw);
    return {
      isConfigured: this.asBoolean(source['isConfigured']),
      canSendMessages: this.asBoolean(source['canSendMessages']),
      providerType: this.asString(source['providerType']) || this.asString(source['provider']),
      senderDisplayName: this.asString(source['senderDisplayName']),
      senderPhoneMasked: this.asString(source['senderPhoneMasked']),
      templatesCount: this.asNumber(source['templatesCount']),
      environmentStatus: this.asString(source['environmentStatus']),
      lastSyncAt: this.asNullableString(source['lastSyncAt']),
      notes: this.asString(source['notes']),
    };
  }

  private mapTemplate(raw: unknown): WhatsAppTemplate {
    const source = this.asRecord(raw);
    const variables = this.asArray(source['variables'])
      .map((entry) => this.asString(entry).trim())
      .filter(Boolean);
    return {
      name: this.asString(source['name']) || this.asString(source['templateName']),
      language: this.asString(source['language']) || this.asString(source['locale']),
      category: this.asString(source['category']),
      status: this.asString(source['status']),
      variables,
      bodyPreview: this.asString(source['bodyPreview']) || this.asString(source['preview']),
    };
  }

  private mapCapabilities(raw: unknown): WhatsAppCapabilities {
    const source = this.asRecord(raw);
    return {
      templateOnly: this.asBoolean(source['templateOnly'], true),
      freeTextEnabled: this.asBoolean(source['freeTextEnabled']),
      inboxEnabled: this.asBoolean(source['inboxEnabled']),
      inboundHumanChatEnabled: this.asBoolean(source['inboundHumanChatEnabled']),
      platformManagedNumber: this.asBoolean(source['platformManagedNumber'], true),
      multipleNumbersPerCompany: this.asBoolean(source['multipleNumbersPerCompany']),
      operationalFocus: this.asBoolean(source['operationalFocus'], true),
    };
  }

  private mapRecentActivity(raw: unknown): WhatsAppRecentActivityItem {
    const source = this.asRecord(raw);
    return {
      id: this.asString(source['id']) || crypto.randomUUID(),
      occurredAt: this.asString(source['occurredAt']) || this.asString(source['createdAt']),
      templateName: this.asString(source['templateName']),
      status: this.asString(source['status']),
      targetType: this.asString(source['targetType']),
      cardId: this.asNullableString(source['cardId']),
      workflowId: this.asNullableString(source['workflowId']),
    };
  }

  private unwrapItems(raw: unknown): unknown {
    const record = this.asRecord(raw);
    if (Array.isArray(raw)) return raw;
    if (Array.isArray(record['items'])) return record['items'];
    if (Array.isArray(record['data'])) return record['data'];
    return [];
  }

  private asRecord(value: unknown): JsonRecord {
    return value && typeof value === 'object' && !Array.isArray(value) ? (value as JsonRecord) : {};
  }

  private asArray(value: unknown): unknown[] {
    return Array.isArray(value) ? value : [];
  }

  private asString(value: unknown): string {
    return typeof value === 'string' ? value : '';
  }

  private asNullableString(value: unknown): string | null {
    const parsed = this.asString(value).trim();
    return parsed ? parsed : null;
  }

  private asBoolean(value: unknown, fallback = false): boolean {
    return typeof value === 'boolean' ? value : fallback;
  }

  private asNumber(value: unknown): number {
    return typeof value === 'number' ? value : 0;
  }
}
